import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useCreditCardSetupContext } from './CreditCardSetUp.context';
import { canEnableCreditCardForCash } from './CreditCardSetup.utils';

type CreditCardForCashEligibility = Readonly<{
  canEnableCreditCard: boolean;
  registryKycLaterEnabled: boolean;
  loading: boolean;
  /**
   * The payout account's currency, when an account exists. Credit-card contributions can only be
   * collected in this currency, so a caller editing a specific fund compares it against that
   * fund's own currency to decide per-fund eligibility (a registry-wide comparison is wrong — each
   * fund carries its own currency).
   */
  payoutCurrencyCode: string | null;
}>;

/**
 * Determines whether credit card payments can be enabled for cash funds
 * based on the payout account's KYC status and the kycLaterForRegistry feature flag.
 *
 * - Flag off: only `readyToUse` accounts can enable credit card
 * - Flag on: `onboarding`, `pending`, and `readyToUse` accounts can enable credit card
 *
 * Currency matching is deliberately NOT decided here: it is per-fund, so callers compare
 * `payoutCurrencyCode` against the specific fund's currency.
 */
export const useCreditCardForCashEligibility = (): CreditCardForCashEligibility => {
  const creditCardSetupContext = useCreditCardSetupContext(false);
  const payoutAccountInfo = creditCardSetupContext?.payoutAccountInfo ?? null;
  const fetchingPayoutInfo = creditCardSetupContext?.fetchingPayoutInfo ?? false;
  const [registryKycLaterEnabled, kycFlagLoading] = useIsFeatureEnabled('registryKycLaterEnabled');

  const loading = fetchingPayoutInfo || kycFlagLoading;
  const canEnableCreditCard = !loading && !!payoutAccountInfo && canEnableCreditCardForCash(payoutAccountInfo.status, registryKycLaterEnabled);

  return { canEnableCreditCard, registryKycLaterEnabled, loading, payoutCurrencyCode: payoutAccountInfo?.currencyCode ?? null };
};
